import { observability } from './services/observability'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './responsive.css'
import App from './App.jsx'
import { registerSW } from 'virtual:pwa-register'
import GlobalErrorBoundary from './components/Common/ErrorBoundary'
import { initStoredMasterTheme } from './services/masterThemes'

// Apparence Master Shell : réapplique le thème persisté (jeu de variables CSS)
// avant le premier rendu — voir src/services/masterThemes.js.
initStoredMasterTheme()

observability.init({
  dsn:                      import.meta.env.VITE_SENTRY_DSN,
  environment:              import.meta.env.MODE,
  tracesSampleRate:         0.2,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
})

// Coupe-circuit entre les deux sources de reload automatique ci-dessous
// (nouveau service worker activé, chunk lazy introuvable) : au plus UN
// reload automatique par onglet PAR CAUSE. Signalé par Alfred, 2026-10-09 :
// /dashboard/ldmp « se recharge indéfiniment » — plusieurs sessions ont
// poussé 6+ déploiements en moins de 3h ce jour-là, et `registerType:
// 'autoUpdate'` recharge SANS GARDE à chaque activation d'un nouveau
// service worker (wb.addEventListener('activated', ...), vite-plugin-pwa).
// Un déploiement qui en rattrape un autre avant que le reload précédent
// n'ait fini déclenchait donc un nouveau reload, indéfiniment tant que les
// déploiements se succédaient plus vite que le chargement de la page.
//
// La clé est PAR CAUSE (et non un seul verrou partagé pour tout l'onglet) :
// un premier déploiement qui déclenche le reload du service worker ne doit
// pas consommer, pour le reste de la session, la capacité de récupération
// d'un chunk lazy DIFFÉRENT qui échoue minutes plus tard (ex: navigation
// vers /dashboard/curriculum-syllabus après le reload SW) — observé le
// 2026-10-12 : le budget unique epuisé par le SW faisait tomber ces pages
// sur l'écran d'erreur critique plein écran au lieu de se réparer seules.
const rechargerUneSeuleFois = (cause) => {
  const CLE = `rectorium:reload-auto-deja-tente:${cause}`
  if (sessionStorage.getItem(CLE)) return
  sessionStorage.setItem(CLE, '1')
  window.location.reload()
}

registerSW({ immediate: true, onNeedReload: () => rechargerUneSeuleFois('sw-update') })

// Auto-réparation après déploiement : si un chunk lazy référencé par une
// version précédente n'existe plus (service worker/HTML périmé), Vite émet
// vite:preloadError — on recharge pour récupérer la version à jour au lieu
// d'afficher l'écran d'erreur critique.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  rechargerUneSeuleFois(`chunk:${event.payload?.message || 'preload'}`)
})

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <GlobalErrorBoundary>
      <App />
    </GlobalErrorBoundary>
  </StrictMode>,
)
